import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { MdClose } from "react-icons/md";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import showToast from "../../helpers/showToast";
import { post, get } from "../../server";
import { useAtom } from "jotai";
import { loggedInUser } from "../../utils/store";
import { PAY_MEMBER_PAYMENT, GET_USER, GET_USER_SUBSCRIPTION } from "../../utils/constants/apiUrl";
import { trackEvent } from "../../utils/mixpanel";

const cardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      iconColor: "#000000",
      fontSize: "14px",
      color: "#000000",
      background: "#ffffff",
      "::placeholder": { color: "#000000" },
    },
    invalid: { color: "#ff0000" },
  },
};

const PendingPaymentModal = ({
  pendingMembers,
  setPendingMember,
  handleCloseModal,
  userTeamId,
  show,
  selectedId,
  teamPackageInfo,
}) => {
  const [loggedUser] = useAtom(loggedInUser);
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const checkbox = useRef();
  const [selectedOption, setSelectedOption] = useState("monthly");

  const handleRadioChange = (event) => {
    setSelectedOption(event.target.value);
  };

  // const loadSubscriptions = async () => {
  //   let { status, message, data } = await get(
  //     PACKAGES_LISTING,
  //     Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
  //   );
  //   if (status) {
  //     if (data) {
  //       let ownerpackages = data.filter(
  //         (singlePackage) =>
  //           singlePackage.package_type === "owner" ||
  //           singlePackage.package_type === "customer_specific_owner"
  //       );
  //       setSubscriptions(ownerpackages);
  //     }
  //   } else {
  //     showToast(status, message);
  //   }
  // };

  useEffect(() => {
    if (!show) return;

    // Original approach: fetched from GET_USER package_details
    // const getSubscriptionDetails = async () => {
    //   let { status, message, data } = await get(
    //     GET_USER,
    //     Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    //   );
    //   if (status) {
    //     if (data && data.package_details && data.package_details.length > 0) {
    //       const teamOwnerPackages = data.package_details.filter(
    //         (pkg) => pkg.is_team_owner === 1
    //       );
    //       if (teamOwnerPackages.length > 0) {
    //         setPerTeamMemberPrice(teamOwnerPackages[0].price_per_user_after_limit);
    //       }
    //     } else {
    //       showToast(false, "No package details available or empty.");
    //     }
    //   } else {
    //     showToast(status, message);
    //   }
    // };
    // getSubscriptionDetails();

    // New approach: use teamPackageInfo prop first, then fallback to API
    if (teamPackageInfo) {
      if (teamPackageInfo.package) {
        // teamPackageInfo is a subscription row — package is nested inside
        setPackageData({
          package_id: teamPackageInfo.package.id,
          package_name: teamPackageInfo.package.package_name,
          price_per_user: Number(
            teamPackageInfo.price_per_user_after_limit ??
            teamPackageInfo.package.price_per_user_after_limit ??
            0
          ),
          subscription_type: teamPackageInfo.subscription_type || null,
          duration_type: teamPackageInfo.package.duration_type || null,
        });
        return;
      } else if (teamPackageInfo.package_name) {
        // teamPackageInfo is a package row (customer_specific_owner shape)
        let price = teamPackageInfo.price_per_user_after_limit;
        let subscriptionType = null;
        const durationType = teamPackageInfo.duration_type || null;

        if (teamPackageInfo.subscriptions && Array.isArray(teamPackageInfo.subscriptions)) {
          const sub = teamPackageInfo.subscriptions.find((s) => s.team_id == userTeamId && s.is_team_owner === 1) ||
                      teamPackageInfo.subscriptions.find((s) => s.team_id != null);
          if (sub && sub.price_per_user_after_limit != null) {
            price = sub.price_per_user_after_limit;
          }
          if (sub?.subscription_type) {
            subscriptionType = sub.subscription_type;
          }
        }
        setPackageData({
          package_id: teamPackageInfo.id,
          package_name: teamPackageInfo.package_name,
          price_per_user: Number(price ?? 0),
          subscription_type: subscriptionType,
          duration_type: durationType,
        });
        return;
      }
    }

    // Fallback: fetch fresh subscription data from API
    const fetchPackage = async () => {
      const payload = { user_id: loggedUser.id, team_id: userTeamId };
      const { status, data } = await post(
        GET_USER_SUBSCRIPTION,
        payload,
        Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
      );
      if (status && data?.[0]) {
        const sub = data[0];
        setPackageData({
          package_id: sub.package_id,
          package_name: sub.package?.package_name ?? "",
          price_per_user: Number(
            sub.price_per_user_after_limit ??
            sub.package?.price_per_user_after_limit ??
            0
          ),
          subscription_type: sub.subscription_type || null,
          duration_type: sub.package?.duration_type || null,
        });
      } else {
        // Last fallback: GET_USER
        const userRes = await get(
          GET_USER,
          Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
        );
        if (userRes.status && userRes.data?.package_details?.length > 0) {
          const pkg =
            userRes.data.package_details.find(
              (p) => p.is_team_owner === 1 && p.team_id == userTeamId
            ) || userRes.data.package_details.find((p) => p.team_id != null);
          if (pkg) {
            setPackageData({
              package_id: pkg.package_id,
              package_name: pkg.package?.package_name ?? "",
              price_per_user: Number(pkg.price_per_user_after_limit ?? 0),
              subscription_type: pkg.subscription_type || null,
              duration_type: pkg.package?.duration_type || null,
            });
          }
        }
      }
    };
    fetchPackage();
  }, [show, userTeamId]);

  const isFree = !packageData || packageData.price_per_user === 0;

  // Determine billing period: prefer subscription_type, fall back to duration_type from package
  const billingPeriod = packageData?.subscription_type || packageData?.duration_type || "monthly";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    if (!stripe || !elements) return;
    setIsLoading(true);

    trackEvent("Stripe Payment Attempt", {
      team_id: userTeamId,
      user_id: selectedId,
      source: window.location.pathname,
      timestamp: new Date().toISOString(),
    });

    try {
      let stripeToken = null;

      if (!isFree) {
        const cardElement = elements.getElement(CardElement);
        const { error: stripeError, token } = await stripe.createToken(cardElement);
        if (stripeError) {
          setIsLoading(false);
          setError(stripeError.message);
          trackEvent("Stripe Payment Failed", {
            error_type: "Stripe Token Error",
            message: stripeError.message,
            team_id: userTeamId,
            user_id: selectedId,
          });
          return;
        }
        stripeToken = token.id;
      }

      const paymentObj = {
        team_id: userTeamId,
        user_id: selectedId,
        subscription_type: billingPeriod,
        stripe_token: stripeToken || "",
        package_id: packageData?.package_id ?? null,
        package_name: packageData?.package_name ?? null,
        amount: packageData?.price_per_user ?? 0,
      };

      const { status, message } = await post(PAY_MEMBER_PAYMENT, paymentObj, loggedUser.token);

      if (!status) {
        setError(message);
        setIsLoading(false);
        trackEvent("Stripe Payment Failed", {
          error_type: "Backend Payment Failure",
          message,
          team_id: userTeamId,
          user_id: selectedId,
        });
        return;
      }

      trackEvent("Stripe Payment Success", {
        team_id: userTeamId,
        user_id: selectedId,
        message,
        source: window.location.pathname,
      });

      setPendingMember(pendingMembers.filter((obj) => obj.id != selectedId));
      setIsLoading(false);
      setError(null);
      handleCloseModal(true);
      showToast(true, message);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      trackEvent("Stripe Payment Failed", {
        error_type: "Unexpected Error",
        message: err.message,
        team_id: userTeamId,
        user_id: selectedId,
      });
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      className="customModal square-modal"
      size="sm"
    >
      <Modal.Header
        className="modalHeader d-flex justify-content-between"
        style={{ borderBottom: "none", width: "fit-content" }}
      >
        <span className="modalHeading">Card Details</span>
        <div className="modalGradientCircle"></div>
      </Modal.Header>

      <Modal.Body>
        <div style={{ marginTop: "10px" }}>
          <form onSubmit={handleSubmit}>
            {!isFree && (
              <div
                style={{
                  border: "2px solid #DAE9F4",
                  padding: "10px",
                  borderRadius: "50px",
                  background: "#fafafa",
                }}
                className="cardElementWrapper"
              >
                <CardElement id="card-element" options={cardElementOptions} />
              </div>
            )}

            <div className="bottomDetail" style={{ marginTop: "16px" }}>
              {packageData?.package_name && (
                <p>
                  Subscription package:
                  <span style={{ paddingLeft: "10px", fontWeight: 600 }}>
                    {packageData.package_name}
                  </span>
                </p>
              )}
              <p>
                Subscription charges:
                <span style={{ paddingLeft: "10px" }}>
                  {isFree ? "Free" : `$${packageData.price_per_user} per/${billingPeriod === "yearly" || billingPeriod === "year" ? "year" : "month"}`}
                </span>
              </p>
            </div>

            <div>
              {error && <p className="errorText">{error}</p>}
              <div
                className="mobileViewWrapper d-block text-end"
                style={{ marginTop: "50px" }}
              >
                <button
                  type="button"
                  onClick={() => { handleCloseModal(); setError(null); }}
                  className="btn btn-outline-primary mx-3"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={(!isFree && !stripe) || isLoading}
                  className="btn btn-primary payBtn"
                >
                  {isLoading ? "Processing..." : "Pay"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PendingPaymentModal;
